/**
 * The 66 books of the Protestant canon, and how a passage names one
 * ("John 1:14-18", "Jn 3:16", "1 Cor 13", "Psalm 23", "Song of Songs 2").
 * Sermon search ("What have we preached on…?", docs/15) finds sermons by
 * book; the series planner offers a book to preach through.
 *
 * `bookPattern` is a regular expression written so Postgres (`~*`) and
 * JavaScript (`new RegExp(…, 'i')`) read it the same way: anchored at the
 * passage's start, so "John 3" is John and "1 John 3" is 1 John.
 */

export interface BibleBook {
  /** "1 Corinthians". */
  name: string
  testament: 'old' | 'new'
  /** Other ways it is written, without the number of a numbered book. */
  aliases: readonly string[]
}

const book = (name: string, testament: 'old' | 'new', aliases: readonly string[] = []) => ({
  name,
  testament,
  aliases,
})

export const BIBLE_BOOKS: readonly BibleBook[] = [
  book('Genesis', 'old', ['gen', 'gn']),
  book('Exodus', 'old', ['exod', 'exo', 'ex']),
  book('Leviticus', 'old', ['lev', 'lv']),
  book('Numbers', 'old', ['num', 'nm']),
  book('Deuteronomy', 'old', ['deut', 'dt']),
  book('Joshua', 'old', ['josh', 'jos']),
  book('Judges', 'old', ['judg', 'jdg']),
  book('Ruth', 'old', ['rth']),
  book('1 Samuel', 'old', ['sam', 'sm']),
  book('2 Samuel', 'old', ['sam', 'sm']),
  book('1 Kings', 'old', ['kgs', 'kings']),
  book('2 Kings', 'old', ['kgs', 'kings']),
  book('1 Chronicles', 'old', ['chron', 'chr']),
  book('2 Chronicles', 'old', ['chron', 'chr']),
  book('Ezra', 'old', ['ezr']),
  book('Nehemiah', 'old', ['neh']),
  book('Esther', 'old', ['esth', 'est']),
  book('Job', 'old', ['jb']),
  book('Psalms', 'old', ['psalm', 'pss', 'ps', 'psa']),
  book('Proverbs', 'old', ['prov', 'prv', 'pr']),
  book('Ecclesiastes', 'old', ['eccles', 'eccl', 'ecc', 'qoheleth']),
  book('Song of Songs', 'old', ['song of solomon', 'song', 'sos', 'canticles']),
  book('Isaiah', 'old', ['isa', 'is']),
  book('Jeremiah', 'old', ['jer']),
  book('Lamentations', 'old', ['lam']),
  book('Ezekiel', 'old', ['ezek', 'eze']),
  book('Daniel', 'old', ['dan', 'dn']),
  book('Hosea', 'old', ['hos']),
  book('Joel', 'old', ['jl']),
  book('Amos', 'old', ['am']),
  book('Obadiah', 'old', ['obad', 'ob']),
  book('Jonah', 'old', ['jon']),
  book('Micah', 'old', ['mic']),
  book('Nahum', 'old', ['nah']),
  book('Habakkuk', 'old', ['hab']),
  book('Zephaniah', 'old', ['zeph', 'zep']),
  book('Haggai', 'old', ['hag']),
  book('Zechariah', 'old', ['zech', 'zec']),
  book('Malachi', 'old', ['mal']),
  book('Matthew', 'new', ['matt', 'mt']),
  book('Mark', 'new', ['mk', 'mrk']),
  book('Luke', 'new', ['lk', 'luk']),
  book('John', 'new', ['jn', 'jhn']),
  book('Acts', 'new', ['act']),
  book('Romans', 'new', ['rom', 'rm']),
  book('1 Corinthians', 'new', ['cor']),
  book('2 Corinthians', 'new', ['cor']),
  book('Galatians', 'new', ['gal']),
  book('Ephesians', 'new', ['eph']),
  book('Philippians', 'new', ['phil', 'php']),
  book('Colossians', 'new', ['col']),
  book('1 Thessalonians', 'new', ['thess', 'thes', 'th']),
  book('2 Thessalonians', 'new', ['thess', 'thes', 'th']),
  book('1 Timothy', 'new', ['tim', 'tm']),
  book('2 Timothy', 'new', ['tim', 'tm']),
  book('Titus', 'new', ['tit']),
  book('Philemon', 'new', ['philem', 'phlm']),
  book('Hebrews', 'new', ['heb']),
  book('James', 'new', ['jas', 'jm']),
  book('1 Peter', 'new', ['pet', 'pt']),
  book('2 Peter', 'new', ['pet', 'pt']),
  book('1 John', 'new', ['jn', 'jhn']),
  book('2 John', 'new', ['jn', 'jhn']),
  book('3 John', 'new', ['jn', 'jhn']),
  book('Jude', 'new', ['jud']),
  book('Revelation', 'new', ['rev', 'revelations', 'apocalypse']),
]

export const BIBLE_BOOK_NAMES: readonly string[] = BIBLE_BOOKS.map((b) => b.name)

const NUMBER_WORDS: Record<string, readonly string[]> = {
  '1': ['1', 'i', '1st', 'first'],
  '2': ['2', 'ii', '2nd', 'second'],
  '3': ['3', 'iii', '3rd', 'third'],
}

const escape = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** The book by its name, any case ("1 corinthians"), or null. */
export function bibleBook(name: string): BibleBook | null {
  const wanted = name.trim().toLowerCase()
  return BIBLE_BOOKS.find((b) => b.name.toLowerCase() === wanted) ?? null
}

/**
 * A passage naming this book, as a regular expression both Postgres and
 * JavaScript read alike: at the start (after spaces), the book's name or an
 * alias — a numbered book's number written any common way first — then an
 * optional full stop, and a chapter, a space or the end.
 */
export function bookPattern(entry: BibleBook): string {
  const numbered = /^([123]) (.+)$/.exec(entry.name)
  const base = numbered ? (numbered[2] ?? entry.name) : entry.name
  const names = [base, ...entry.aliases]
    .map((n) => n.toLowerCase())
    .sort((a, b) => b.length - a.length)
    .map((n) => escape(n).replace(/ /g, '\\s+'))
  const words = `(?:${names.join('|')})`
  const head = numbered
    ? `(?:${(NUMBER_WORDS[numbered[1] ?? '1'] ?? []).join('|')})\\s*${words}`
    : words
  return `^\\s*${head}\\.?(?:\\s|\\d|$)`
}

/** The book a passage is from, or null ("John 1:14" → John; "1 Jn 4:8" → 1 John). */
export function passageBook(passage: string): BibleBook | null {
  for (const entry of BIBLE_BOOKS)
    if (new RegExp(bookPattern(entry), 'i').test(passage)) return entry
  return null
}

/** Is this passage in that book? */
export function passageInBook(passage: string, entry: BibleBook): boolean {
  return new RegExp(bookPattern(entry), 'i').test(passage)
}
